import { Link, Outlet, Navigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiFehler } from "./api";

/**
 * Rahmen und Zugangsschutz. Jede API-Route verlangt eine Sitzung; ist keine
 * da, antwortet der Server mit 401 und wir schicken zur Anmeldung.
 */
export default function App() {
  const ort = useLocation();
  const client = useQueryClient();

  const { data: benutzer, isPending, error } = useQuery({
    queryKey: ["ich"],
    queryFn: api.ich,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (isPending) {
    return <p className="p-8 text-black/40">lädt …</p>;
  }

  if (error instanceof ApiFehler && error.status === 401) {
    return <Navigate to="/anmelden" replace state={{ von: ort.pathname }} />;
  }
  if (error) {
    return (
      <div className="p-8">
        <p className="text-red-700">Die API ist nicht erreichbar.</p>
        <p className="mt-2 text-sm text-black/50">
          Läuft <code>npm run dev:api</code>? ({(error as Error).message})
        </p>
      </div>
    );
  }

  async function abmelden() {
    await api.abmelden();
    client.clear();
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-black/10 bg-white/60 backdrop-blur dark:bg-black/30">
        {/*
          Zwei Zeilen am schmalen Gerät, eine am breiten (`sm:`).
          Horizontales Scrollen allein reichte nicht: in einer Zeile
          schiebt das Menü Name und „abmelden" nach rechts aus dem Blick,
          und dorthin scrollt niemand. Oben also Titel und Zugang, darunter
          das Menü — und das scrollt, wenn es nicht passt.
        */}
        <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex items-center gap-4">
            <Link to="/suche" className="text-lg font-semibold tracking-tight">
              Stammbaum
            </Link>
            <span className="flex-1 sm:hidden" />
            <Link
              to="/zugang"
              className="text-sm text-black/50 hover:underline sm:hidden dark:text-white/50"
            >
              {benutzer?.anzeigename}
            </Link>
            <button
              onClick={abmelden}
              className="text-sm text-black/50 hover:underline sm:hidden dark:text-white/50"
            >
              abmelden
            </button>
          </div>
          {/*
            `overflow-x-auto` mit `whitespace-nowrap`: die Punkte brechen
            nicht um, sondern wandern unter den Finger. `-mx-1 px-1` gibt
            dem ersten und letzten Punkt Luft, damit sie am Rand nicht
            angeschnitten aussehen.
          */}
          <nav className="-mx-1 flex flex-1 gap-4 overflow-x-auto px-1 text-sm whitespace-nowrap">
            <Link to="/suche" className="hover:underline">
              Personen
            </Link>
            {benutzer?.personId && (
              <>
                <Link to={`/person/${benutzer.personId}`} className="hover:underline">
                  Ich
                </Link>
                <Link to={`/baum/${benutzer.personId}`} className="hover:underline">
                  Mein Stammbaum
                </Link>
              </>
            )}
            <Link to="/orte" className="hover:underline">
              Orte
            </Link>
            <Link to="/medien" className="hover:underline">
              Medien
            </Link>
            <Link to="/karte" className="hover:underline">
              Karte
            </Link>
            {/* Die Verwaltung sieht nur der Besitzer. Das ist Bequemlichkeit,
                nicht die Absicherung - die steht in routes/benutzer.ts. */}
            {benutzer?.rolle === "besitzer" && (
              <Link to="/benutzer" className="hover:underline">
                Benutzer
              </Link>
            )}
          </nav>
          {/* Am breiten Gerät rechts in derselben Zeile, am schmalen steht
              es schon oben. */}
          <Link
            to="/zugang"
            className="hidden text-sm text-black/50 hover:underline sm:block dark:text-white/50"
          >
            {benutzer?.anzeigename}
            {benutzer?.rolle === "besitzer" && " · Besitzer"}
          </Link>
          <button
            onClick={abmelden}
            className="hidden text-sm text-black/50 hover:underline sm:block dark:text-white/50"
          >
            abmelden
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
